// Import necessary dependencies and components
import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Row, Col, Badge } from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import { fetchActionsData } from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";

// Define the functional component
function ListScreen(props) {
  // State to hold actions data
  const [actionsData, setActionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);

  // Function to fetch actions data
  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchActionsData(nbr, searchTerm);
      setActionsData(data.data);
    } catch (error) {
      console.error("Error fetching actions data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update search term and fetch data
  const handleSearch = (term) => {
    setSearchTerm(term);
    setNbr(1); // Reset to first page
  };

  // useEffect to automatically load data when the component mounts
  useEffect(() => {
    fetchData();
  }, [nbr, searchTerm]);

  // Memoized columns for the DynamicTable component
  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      {
        Header: "Utilisateur",
        accessor: "user",
        Cell: ({ row }) => {
          const user = row.original.user;
          return user ? `${user.prenom || ''} ${user.nom || ''} (${user.email || ''})` : 'N/A';
        }
      },
      {
        Header: "Événement",
        accessor: "event",
        Cell: ({ value }) => {
          const eventColors = {
            'created': 'bg-success',
            'updated': 'bg-warning',
            'deleted': 'bg-danger'
          };
          const eventLabels = {
            'created': 'Création',
            'updated': 'Modification',
            'deleted': 'Suppression'
          };
          return (
            <Badge className={eventColors[value] || 'bg-info'}>
              {eventLabels[value] || value}
            </Badge>
          );
        }
      },
      {
        Header: "Modèle",
        accessor: "auditable_type",
        Cell: ({ value }) => {
          // Extraire juste le nom du modèle (ex: App\Models\User -> User)
          const modelName = value ? value.split('\\').pop() : 'N/A';
          return modelName;
        }
      },
      { Header: "ID Entité", accessor: "auditable_id" },
      { Header: "URL", accessor: "url" },
      { Header: "IP", accessor: "ip_address" },
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) =>
          format(new Date(value), "dd-MM-yyyy HH:mm:ss", { locale: fr }),
      },
    ].filter(Boolean);
  }, [nbr]);

  // Render loading message while data is being fetched
  if (!actionsData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  // Render the component
  return (
    <Container>
      <div className="actionsList">
        {/* Breadcrumb navigation */}
        <p className="h6">Accueil / Administration / Actions API (RGPD)</p>

        {/* Header and filters */}
        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            <h1 className="p-1">Actions API (RGPD)</h1>
          </Col>
        </Row>
        <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
        {/* Render the DynamicTable component */}
        {loading ? (
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            data={actionsData?.data}
            link={actionsData?.links}
            last_page={actionsData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={actionsData?.total}
            name="action"
          />
        )}
      </div>
    </Container>
  );
}

// Export the component as the default export
export default ListScreen;
